import React, { createContext, useContext, useEffect, useState } from "react";
import api, { setAuthToken, getAuthToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);     // user object or false (not authed)
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    // Only probe session on admin routes — keeps the public site console clean.
    const isAdminRoute = typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
    if (!isAdminRoute) {
      setUser(false);
      setLoading(false);
      return;
    }
    // If neither a stored token nor a cookie are present we can skip the network
    // probe entirely — saves a 401 round-trip on the login screen.
    if (!getAuthToken() && !document.cookie.includes("access_token")) {
      setUser(false);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (mounted) setUser(data);
      } catch {
        if (mounted) {
          setAuthToken(null);
          setUser(false);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.access_token) setAuthToken(data.access_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Logout should never block the UI even if the network call fails.
      // We still wipe the local user state below; only log in development.
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("Logout request failed; clearing local session anyway.", err);
      }
    }
    setAuthToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
